const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '247963065378-7ag7v98m7oboemjvppa105la64orcp25.apps.googleusercontent.com';
const SCOPE = 'https://www.googleapis.com/auth/drive.appdata';

export const googleDriveService = {
  getToken: (): string | null => {
    return sessionStorage.getItem('libritui_drive_access_token');
  },

  setToken: (token: string | null) => {
    if (token) {
      sessionStorage.setItem('libritui_drive_access_token', token);
      sessionStorage.setItem('libritui_drive_token_timestamp', Date.now().toString());
    } else {
      sessionStorage.removeItem('libritui_drive_access_token');
      sessionStorage.removeItem('libritui_drive_token_timestamp');
    }
  },

  checkAndRefreshToken: async (): Promise<string | null> => {
    const token = googleDriveService.getToken();
    if (!token) return null;

    const timestampStr = sessionStorage.getItem('libritui_drive_token_timestamp');
    if (timestampStr) {
      const generatedAt = parseInt(timestampStr, 10);
      const now = Date.now();
      const ONE_HOUR = 60 * 60 * 1000;
      // Refresh if it's older than 55 minutes to be safe
      if (now - generatedAt < ONE_HOUR - 5 * 60 * 1000) {
        return token; // Token is still valid
      }
    }

    // Attempt silent refresh
    try {
      await googleDriveService.loadGapiAndGis();
    } catch (e) {
      console.error('Failed to load Google SDK during token refresh:', e);
      return null;
    }

    return new Promise((resolve) => {
      try {
        if (!(window as any).google?.accounts?.oauth2) {
          resolve(null);
          return;
        }

        const client = (window as any).google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID,
          scope: SCOPE,
          ux_mode: 'popup',
          callback: (response: any) => {
            if (response.error || !response.access_token) {
              googleDriveService.disconnect();
              resolve(null);
              return;
            }
            googleDriveService.setToken(response.access_token);
            resolve(response.access_token);
          },
        });
        client.requestAccessToken({ prompt: '' });
      } catch (e) {
        console.error('Silent token refresh failed', e);
        resolve(null);
      }
    });
  },

  getUserInfo: (): any | null => {
    const data = sessionStorage.getItem('libritui_drive_user_info');
    return data ? JSON.parse(data) : null;
  },

  setUserInfo: (info: any | null) => {
    if (info) {
      sessionStorage.setItem('libritui_drive_user_info', JSON.stringify(info));
    } else {
      sessionStorage.removeItem('libritui_drive_user_info');
    }
  },

  loadGapiAndGis: (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).gapi && (window as any).google && (window as any).google.accounts && (window as any).google.accounts.oauth2) {
        resolve();
        return;
      }

      // 1. GIS Script
      let gisScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (!gisScript) {
        gisScript = document.createElement('script');
        gisScript.setAttribute('src', 'https://accounts.google.com/gsi/client');
        gisScript.setAttribute('async', 'true');
        gisScript.setAttribute('defer', 'true');
        document.body.appendChild(gisScript);
      }

      // 2. GAPI Script
      let gapiScript = document.querySelector('script[src="https://apis.google.com/js/api.js"]');
      if (!gapiScript) {
        gapiScript = document.createElement('script');
        gapiScript.setAttribute('src', 'https://apis.google.com/js/api.js');
        gapiScript.setAttribute('async', 'true');
        gapiScript.setAttribute('defer', 'true');
        document.body.appendChild(gapiScript);
      }

      const checkInterval = setInterval(() => {
        if ((window as any).google && (window as any).google.accounts && (window as any).google.accounts.oauth2) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Timeout after 10s
      setTimeout(() => {
        clearInterval(checkInterval);
        if ((window as any).google && (window as any).google.accounts && (window as any).google.accounts.oauth2) {
          resolve();
        } else {
          reject(new Error("Timeout loading Google SDKs. Please check internet connection."));
        }
      }, 10000);
    });
  },

  connect: (onSuccess: (token: string, userInfo: any) => void, onError?: (err: any) => void) => {
    if (typeof window === 'undefined' || !(window as any).google || !(window as any).google.accounts || !(window as any).google.accounts.oauth2) {
      if (onError) onError(new Error('Google SDK not loaded yet.'));
      return;
    }

    try {
      const client = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        ux_mode: 'popup',
        callback: async (response: any) => {
          if (response.error) {
            if (onError) onError(response);
            return;
          }
          if (response.access_token) {
            const token = response.access_token;
            googleDriveService.setToken(token);
            try {
              const userInfo = await googleDriveService.fetchUserInfo(token);
              googleDriveService.setUserInfo(userInfo);
              onSuccess(token, userInfo);
            } catch (e) {
              // Fallback if userInfo fetch fails
              onSuccess(token, null);
            }
          }
        },
      });

      client.requestAccessToken({ prompt: 'consent' });
    } catch (e) {
      if (onError) onError(e);
    }
  },

  disconnect: () => {
    const token = googleDriveService.getToken();
    if (token && typeof window !== 'undefined' && (window as any).google?.accounts?.oauth2) {
      try {
        (window as any).google.accounts.oauth2.revoke(token, () => {});
      } catch (e) {}
    }
    googleDriveService.setToken(null);
    googleDriveService.setUserInfo(null);
    localStorage.removeItem('libritui_drive_folder_id');
  },

  getFolderId: (): string | null => {
    return localStorage.getItem('libritui_drive_folder_id');
  },

  setFolderId: (id: string) => {
    localStorage.setItem('libritui_drive_folder_id', id);
  },

  fetchUserInfo: async (token: string) => {
    const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.json();
  },

  checkFileExists: async (fileName: string): Promise<string | null> => {
    const token = await googleDriveService.checkAndRefreshToken();
    if (!token) return null;

    try {
      const query = encodeURIComponent(`name = '${fileName.replace(/'/g, "\\'")}' and trashed = false`);
      const url = `https://www.googleapis.com/drive/v3/files?spaces=appDataFolder&q=${query}&fields=files(id,name)`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 401) {
        console.error('[GoogleDriveService] 401 Unauthorized detected during search. Disconnecting.');
        googleDriveService.disconnect();
        return null;
      }

      const data = await res.json();
      if (data.files && data.files.length > 0) {
        return data.files[0].id;
      }
    } catch (e) {
      console.warn('[GoogleDriveService] Error checking file existence:', e);
    }
    return null;
  },

  uploadFile: async (fileName: string, fileBlob: Blob, mimeType: string, bookId?: string): Promise<string | null> => {
    const token = await googleDriveService.checkAndRefreshToken();
    if (!token) return null;

    try {
      const existingId = await googleDriveService.checkFileExists(fileName);

      const metadata: any = {
        name: fileName
      };
      if (!existingId) {
        metadata.parents = ['appDataFolder'];
      }
      if (bookId) {
        metadata.appProperties = { bookId };
      }

      const boundary = '3d9f776d85cc1d';
      const delimiter = `--${boundary}\r\n`;
      const closeDelim = `\r\n--${boundary}--`;

      const metadataHeader = 
        delimiter +
        'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
        JSON.stringify(metadata) +
        '\r\n' +
        delimiter +
        `Content-Type: ${mimeType}\r\n\r\n`;

      const encoder = new TextEncoder();
      const metadataBytes = encoder.encode(metadataHeader);
      const closeBytes = encoder.encode(closeDelim);

      const fileBuffer = await fileBlob.arrayBuffer();
      const fileBytes = new Uint8Array(fileBuffer);

      const bodyBlob = new Blob([metadataBytes, fileBytes, closeBytes], {
        type: `multipart/related; boundary=${boundary}`
      });

      let url = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
      let method = 'POST';

      if (existingId) {
        url = `https://www.googleapis.com/upload/drive/v3/files/${existingId}?uploadType=multipart`;
        method = 'PATCH';
      }

      const res = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: bodyBlob
      });

      if (res.status === 401) {
        console.error('[GoogleDriveService] 401 Unauthorized detected during upload. Disconnecting.');
        googleDriveService.disconnect();
        return null;
      }

      const data = await res.json();
      if (data.id) {
        console.log('[GoogleDriveService] File saved/updated in Google Drive appDataFolder:', data.id);
        return data.id;
      } else {
        console.warn('[GoogleDriveService] Upload API returned error payload:', data);
      }
    } catch (e) {
      console.error('[GoogleDriveService] Error uploading file:', e);
    }
    return null;
  },

  searchOrCreateFolder: async (token: string): Promise<string> => {
    const cachedId = googleDriveService.getFolderId();
    if (cachedId) return cachedId;

    const folderName = 'LibriTui_Library';

    // 1. Buscar pasta existente
    const searchRes = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false&fields=files(id)`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const searchData = await searchRes.json();

    if (searchData.files && searchData.files.length > 0) {
      const id = searchData.files[0].id;
      googleDriveService.setFolderId(id);
      return id;
    }

    // 2. Criar pasta se não existir
    const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: folderName,
        mimeType: 'application/vnd.google-apps.folder'
      })
    });
    const createData = await createRes.json();
    googleDriveService.setFolderId(createData.id);
    return createData.id;
  },

  syncFromDrive: async (
    onProgress?: (current: number, total: number, fileName: string) => void
  ): Promise<{ importedCount: number; errors: string[] }> => {
    try {
      await googleDriveService.loadGapiAndGis();
    } catch (e) {
      console.error('Failed to load Google SDK in syncFromDrive:', e);
    }

    const token = await googleDriveService.checkAndRefreshToken();
    if (!token) {
      return { importedCount: 0, errors: ['Token do Google Drive expirado ou inválido.'] };
    }

    const errors: string[] = [];
    let importedCount = 0;

    try {
      const { storageService } = await import('./storageService');
      const { fileStorageService } = await import('./fileStorageService');

      // 1. List files in appDataFolder
      const fields = encodeURIComponent('files(id,name,mimeType,appProperties,createdTime,modifiedTime)');
      const url = `https://www.googleapis.com/drive/v3/files?spaces=appDataFolder&fields=${fields}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error(`Erro ao listar arquivos do Drive: ${res.statusText}`);
      }

      const data = await res.json();
      const files = data.files || [];

      if (files.length === 0) {
        console.log('[BYOS] Nenhum arquivo encontrado no Google Drive appDataFolder.');
        return { importedCount: 0, errors: [] };
      }

      console.log(`[BYOS] Encontrados ${files.length} arquivos no Google Drive. Iniciando reconciliação...`);

      const localBooks = storageService.getBooks();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = file.name;
        const fileId = file.id;
        const mimeType = file.mimeType;

        // Derive format
        let format = 'pdf';
        if (mimeType === 'application/epub+zip' || fileName.toLowerCase().endsWith('.epub')) {
          format = 'epub';
        } else if (mimeType === 'text/html' || fileName.toLowerCase().endsWith('.html') || fileName.toLowerCase().endsWith('.htm')) {
          format = 'html';
        }

        // Determine bookId
        let bookId = file.appProperties?.bookId;
        if (!bookId) {
          // Fallback stable ID from filename to prevent duplicate downloads
          const cleanName = fileName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
          bookId = `byos-${cleanName}`;
        }

        if (onProgress) {
          onProgress(i + 1, files.length, fileName);
        }

        // Check if book binary exists in local IndexedDB
        const localFile = await fileStorageService.getFile(bookId);
        // Also check if book metadata exists in localStorage
        const bookExists = localBooks.some(b => b.id === bookId);

        if (localFile && bookExists) {
          // Both binary and metadata exist! Skip.
          console.log(`[BYOS] Livro já existe localmente: ${fileName} (ID: ${bookId}). Ignorando.`);
          continue;
        }

        try {
          let fileBlob = localFile;

          if (!fileBlob) {
            console.log(`[BYOS] Baixando do Drive: ${fileName} (ID: ${bookId})`);
            const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
            const downloadRes = await fetch(downloadUrl, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (!downloadRes.ok) {
              throw new Error(`Erro ao obter binário: ${downloadRes.statusText}`);
            }

            fileBlob = await downloadRes.blob();

            // Save binary to IndexedDB
            await fileStorageService.saveFile(bookId, fileBlob);
          } else {
            console.log(`[BYOS] Binário já existe localmente para ${fileName} (ID: ${bookId}). Reidratando apenas metadados.`);
          }

          // Save MIME type
          localStorage.setItem(`mime_${bookId}`, mimeType);

          // Rehydrate book metadata
          const title = fileName.replace(/\.(pdf|html?|epub)$/i, '');
          const author = format === 'pdf' ? 'Documento PDF' : (format === 'epub' ? 'Livro EPUB' : 'Documento HTML');
          const coverUrl = `generated://${bookId}`;
          const fileUrl = 'indexeddb://' + bookId;
          const dateAdded = file.createdTime ? new Date(file.createdTime).getTime() : Date.now();

          storageService.saveBook({
            id: bookId,
            title,
            author,
            coverUrl,
            fileUrl,
            lastPage: 1,
            totalPages: 0,
            dateAdded,
            category: format === 'pdf' ? 'Meus PDFs' : (format === 'epub' ? 'Meus EPUBs' : 'Meus HTMLs'),
            isFavorite: false,
            order: 0,
            language: 'pt',
            format
          });

          importedCount++;
          console.log(`[BYOS] Livro reidratado com sucesso: ${fileName}`);
        } catch (downloadErr: any) {
          console.error(`[BYOS] Falha ao sincronizar o livro ${fileName}:`, downloadErr);
          errors.push(`Erro ao baixar ${fileName}: ${downloadErr.message || downloadErr}`);
        }
      }
    } catch (e: any) {
      console.error('[BYOS] Erro em syncFromDrive:', e);
      errors.push(`Erro de sincronização: ${e.message || e}`);
    }

    return { importedCount, errors };
  }
};
